import { Controller } from "@hotwired/stimulus"

// Docs image lightbox. Turns every <img> inside the rendered article into
// a click-to-zoom target. Opens a full-viewport overlay with the original
// image, click/esc to close.
//
// Wired from the `article` element in show.html.erb via
// `data-controller="docs--lightbox"`.
//
// No backend coordination needed — everything happens client-side against
// the images already in the DOM.
export default class extends Controller {
  static CONTAINER_ID = "docs-lightbox-overlay"

  connect() {
    this._onClick = this._onClick.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    this._attach()
  }

  disconnect() {
    this._detach()
    this._close()
  }

  _attach() {
    this.element.querySelectorAll("img").forEach((img) => {
      // Skip YouTube thumbnails: they're wrapped in <a> links that open the
      // video — don't intercept those.
      if (img.closest("a[href^='https://youtu'], a[href*='youtube.com']"))
        return
      img.style.cursor = "zoom-in"
      img.addEventListener("click", this._onClick)
    })
    document.addEventListener("keydown", this._onKeydown)
  }

  _detach() {
    this.element.querySelectorAll("img").forEach((img) => {
      img.removeEventListener("click", this._onClick)
    })
    document.removeEventListener("keydown", this._onKeydown)
  }

  _onClick(event) {
    const img = event.currentTarget
    if (!img || !img.src) return
    event.preventDefault()
    this._open(img.src, img.alt || "")
  }

  _onKeydown(event) {
    if (event.key === "Escape") this._close()
  }

  _open(src, alt) {
    this._close()
    const overlay = document.createElement("div")
    overlay.id = this.constructor.CONTAINER_ID
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.setAttribute("aria-label", alt || "Image preview")
    overlay.className =
      "fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm cursor-zoom-out"
    overlay.addEventListener("click", () => this._close())

    const img = document.createElement("img")
    img.src = src
    img.alt = alt
    img.className =
      "max-h-full max-w-full rounded-lg border border-white/10 shadow-2xl"
    img.addEventListener("click", (ev) => ev.stopPropagation())
    overlay.appendChild(img)

    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.setAttribute("aria-label", "Close preview")
    closeBtn.className =
      "absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
    closeBtn.textContent = "✕"
    closeBtn.addEventListener("click", (ev) => {
      ev.stopPropagation()
      this._close()
    })
    overlay.appendChild(closeBtn)

    document.body.appendChild(overlay)
    document.body.style.overflow = "hidden"
  }

  _close() {
    const existing = document.getElementById(this.constructor.CONTAINER_ID)
    if (existing) existing.remove()
    document.body.style.overflow = ""
  }
}
